import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../auth/AuthContext';
import ModuleShell from '../../components/ModuleShell';
import PeriodDelta from '../../components/PeriodDelta';
import RefreshRollupButton, {
  useRollupRefreshMessage,
} from '../../components/RefreshRollupButton';
import Sparkline from '../../components/Sparkline';
import useCurrentAndPriorQuery from '../../hooks/useCurrentAndPriorQuery';
import {
  ADMIN_JOB_STATUS,
  ADMIN_KPI_SUMMARY,
  ADMIN_MRR_SUMMARY,
  ADMIN_PAYMENT_FAIL_SUMMARY,
  ADMIN_PRODUCT_ANALYTICS,
  ADMIN_SIGNUP_SOURCES,
  ADMIN_SIGNUPS_BY_TIER_IN_RANGE,
} from '../../graphql/adminQueries';
import { STRIPE_PAID_SUBSCRIPTION_INTEGRITY_CAPTION } from '../../metrics/customerMetricsIntegrity';
import { largestAdjacentDrop } from '../product/productDiagnosis';
import { PAID_FUNNEL_STEPS } from '../product/ProductModule';
import { formatCurrency, formatNumber } from '../../utils/format';
import { RangeSelect, useDateRange } from './DateRangeContext';
import {
  buildHomeWeeklyMemo,
  formatPaidFunnelDropMemo,
  homeAttention,
  isGrowthSpendChecklistEnabled,
  parseWeeklyChecklistState,
  serializeWeeklyChecklistChecked,
  signupMixHint,
  splitSignupTiers,
  toggleWeeklyChecklistItem,
  topSignupSources,
  weeklyChecklistCaption,
  weeklyChecklistItemDefinitions,
  weeklyChecklistStorageKey,
} from './homeSnapshot';

const RUNBOOK_URL =
  'https://gitlab.com/null-space-productions/null-world-productions/fiscal-flowcast/-/blob/main/company-handbook/docs/marketing/runbooks/weekly-gtm-review.md';

const checklistEnv = process.env.REACT_APP_ENV || 'local';

export default function HomeModule() {
  const { isAdmin } = useAuth();
  const { range } = useDateRange();
  const [refreshMessage, setRefreshMessage] = useRollupRefreshMessage();
  const [memoStatus, setMemoStatus] = useState('');

  const checklistItems = useMemo(
    () => weeklyChecklistItemDefinitions(undefined, { isAdmin }),
    [isAdmin]
  );
  const checklistCaption = useMemo(
    () => weeklyChecklistCaption(checklistItems, { isAdmin }),
    [checklistItems, isAdmin]
  );
  const checklistStorageKey = useMemo(
    () => weeklyChecklistStorageKey(checklistEnv),
    []
  );
  const [checklistChecked, setChecklistChecked] = useState(() =>
    parseWeeklyChecklistState(null, checklistItems)
  );

  useEffect(() => {
    try {
      const raw = localStorage.getItem(checklistStorageKey);
      setChecklistChecked(parseWeeklyChecklistState(raw, checklistItems));
    } catch {
      setChecklistChecked(parseWeeklyChecklistState(null, checklistItems));
    }
  }, [checklistStorageKey, checklistItems]);

  const persistChecklist = useCallback(
    (nextChecked) => {
      try {
        localStorage.setItem(
          checklistStorageKey,
          JSON.stringify(serializeWeeklyChecklistChecked(nextChecked, checklistItems))
        );
      } catch {
        /* ignore quota / private mode */
      }
    },
    [checklistStorageKey, checklistItems]
  );

  const handleChecklistToggle = (itemId) => {
    const next = toggleWeeklyChecklistItem(checklistChecked, itemId, checklistItems);
    setChecklistChecked(next);
    persistChecklist(next);
  };

  const {
    data: kpiData,
    priorData: priorKpiData,
    loading: kpiLoading,
    error: kpiError,
    refetch: refetchKpi,
  } = useCurrentAndPriorQuery(ADMIN_KPI_SUMMARY, range);

  const { data: freshnessData, refetch: refetchFreshness } = useQuery(ADMIN_JOB_STATUS);

  const {
    data: tierData,
    loading: tierLoading,
    error: tierError,
    refetch: refetchTiers,
  } = useQuery(ADMIN_SIGNUPS_BY_TIER_IN_RANGE, { variables: { range } });

  const {
    data: sourceData,
    loading: sourceLoading,
    error: sourceError,
    refetch: refetchSources,
  } = useQuery(ADMIN_SIGNUP_SOURCES, { variables: { range } });

  const {
    data: mrrData,
    loading: mrrLoading,
    error: mrrError,
    refetch: refetchMrr,
  } = useQuery(ADMIN_MRR_SUMMARY, { variables: { range } });

  const { data: productAnalyticsData } = useQuery(ADMIN_PRODUCT_ANALYTICS, {
    variables: { range },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const { data: failSummaryData } = useQuery(ADMIN_PAYMENT_FAIL_SUMMARY, {
    variables: { range },
    skip: !isAdmin,
    errorPolicy: 'all',
  });

  const kpi = kpiData?.adminKpiSummary;
  const priorKpi = priorKpiData?.adminKpiSummary;
  const freshness = freshnessData?.adminJobStatus;
  const mix =
    tierError || tierLoading || !tierData
      ? null
      : splitSignupTiers(tierData.adminSignupsByTierInRange);
  const sources = topSignupSources(sourceData?.adminSignupSources);
  const mrr = mrrData?.adminMrrSummary;
  const alerts = homeAttention({
    freshness,
    signups: kpiLoading ? undefined : kpi?.signups,
    violations: kpi?.violations,
    priorViolations: priorKpi?.violations,
  });

  const paidFunnelDrop = useMemo(() => {
    if (!productAnalyticsData?.adminProductAnalytics?.funnel) return null;
    return largestAdjacentDrop(
      PAID_FUNNEL_STEPS,
      productAnalyticsData.adminProductAnalytics.funnel
    );
  }, [productAnalyticsData]);

  const paymentFailCount = isAdmin ? failSummaryData?.adminPaymentFailSummary?.count : null;

  const refetchAll = () =>
    Promise.all([
      refetchKpi(),
      refetchFreshness(),
      refetchTiers(),
      refetchSources(),
      refetchMrr(),
    ]);

  const handleCopyMemo = async () => {
    const memo = buildHomeWeeklyMemo({
      kpi: { totalUsers: kpi?.totalUsers, signups: kpi?.signups },
      mix,
      topSources: sources,
      mrr,
      paidFunnelDrop: formatPaidFunnelDropMemo(paidFunnelDrop),
      paymentFailCount,
      growthSpendChecklistEnabled: isGrowthSpendChecklistEnabled(checklistItems),
    });
    try {
      await navigator.clipboard.writeText(memo);
      setMemoStatus('Memo copied — paste into Slack.');
    } catch {
      window.prompt('Copy weekly memo:', memo);
      setMemoStatus('Copy the memo from the dialog if clipboard is blocked.');
    }
  };

  return (
    <ModuleShell
      title="Executive KPIs"
      description="Customers, signups, and MRR for the weekly review."
      leading={
        <div className="page-toolbar">
          <div className="control-cluster">
            <RangeSelect id="home-range" />
            <RefreshRollupButton onMessage={setRefreshMessage} onRefreshed={refetchAll} />
            <button type="button" className="secondary-button" onClick={handleCopyMemo}>
              Copy memo
            </button>
          </div>
        </div>
      }
      loading={kpiLoading}
      loadingLabel="Loading KPIs…"
      error={kpiError}
      errorLabel="Could not load KPI data. Confirm API admin queries are deployed."
    >
      {refreshMessage && <p className="module-status">{refreshMessage}</p>}
      {memoStatus && <p className="module-status">{memoStatus}</p>}

      <section className="chart-card home-weekly-checklist" aria-label="Weekly gaps checklist">
        <h2>Weekly gaps checklist</h2>
        <p className="chart-hint">{checklistCaption}</p>
        <ul className="home-checklist">
          {checklistItems.map((item) => (
            <li key={item.id}>
              <label>
                <input
                  type="checkbox"
                  checked={checklistChecked[item.id] === true}
                  disabled={!item.enabled}
                  onChange={() => handleChecklistToggle(item.id)}
                />
                {item.label}
              </label>
            </li>
          ))}
        </ul>
      </section>
      {alerts.length > 0 ? (
        <section className="home-attention" aria-label="Needs attention">
          <h2>Needs attention</h2>
          <ul>
            {alerts.map((alert) => (
              <li key={alert}>{alert}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="kpi-grid kpi-grid-home">
        <article className="kpi-card">
          <p className="kpi-label">Customers</p>
          <p className="kpi-value">{formatNumber(kpi?.totalUsers)}</p>
          <PeriodDelta current={kpi?.totalUsers} prior={priorKpi?.totalUsers} mode="count" />
          <p className="kpi-hint">Excludes test and internal accounts</p>
        </article>
        <article className="kpi-card">
          <p className="kpi-label">Signups</p>
          <p className="kpi-value">{formatNumber(kpi?.signups)}</p>
          <PeriodDelta current={kpi?.signups} prior={priorKpi?.signups} mode="count" />
          <p className="kpi-hint">
            {tierError
              ? 'Could not load paid and free split.'
              : tierLoading || !mix
                ? 'Loading paid and free…'
                : signupMixHint(mix)}
          </p>
        </article>
        <article className="kpi-card">
          <p className="kpi-label">Paid signups</p>
          <p className="kpi-value">{mix ? formatNumber(mix.paid) : '—'}</p>
          <p className="kpi-hint">Basic, Premium, and Enterprise</p>
        </article>
        <article className="kpi-card">
          <p className="kpi-label">MRR</p>
          <p className="kpi-value">
            {mrrError || mrrLoading ? '—' : formatCurrency(mrr?.mrrCents, mrr?.currency)}
          </p>
          <p className="kpi-hint">
            {mrrError
              ? 'Could not load MRR.'
              : mrrLoading
                ? 'Loading MRR…'
                : mrr
                  ? `${formatNumber(mrr.activeSubscriptions)} paying (Stripe joined to customer users)`
                  : 'No Stripe snapshot'}
          </p>
          {!mrrError && !mrrLoading && mrr ? (
            <p className="kpi-hint">{STRIPE_PAID_SUBSCRIPTION_INTEGRITY_CAPTION}</p>
          ) : null}
        </article>
      </div>

      <section className="chart-card home-wau">
        <h2>WAU</h2>
        <p className="kpi-value">{formatNumber(kpi?.wau)}</p>
        <PeriodDelta current={kpi?.wau} prior={priorKpi?.wau} mode="count" />
        <p className="kpi-hint">Trailing 7 days via lastLogin</p>
        <Sparkline
          series={kpi?.series}
          field="activeUsers"
          formatValue={(value) => `${formatNumber(value)} active that day`}
          viewBox="0 0 100 36"
        />
      </section>

      <section className="chart-card">
        <div className="home-section-head">
          <h2>Signup sources</h2>
          <Link to="/growth">All sources</Link>
        </div>
        <p className="chart-hint">Top three first-touch sources in this range.</p>
        {sourceError ? (
          <p className="chart-hint">Could not load signup sources.</p>
        ) : sourceLoading ? (
          <p className="chart-hint">Loading signup sources…</p>
        ) : sources.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Source</th>
                <th scope="col">Signups</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((row) => (
                <tr key={row.source}>
                  <td>{row.source}</td>
                  <td>{formatNumber(row.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="chart-empty">No signups in range.</p>
        )}
      </section>

      <section className="quick-links">
        <h2>Quick links</h2>
        <ul>
          <li>
            <a href="https://dashboard.stripe.com/" target="_blank" rel="noreferrer">
              Stripe
            </a>
          </li>
          <li>
            <a href="https://analytics.google.com/" target="_blank" rel="noreferrer">
              GA4
            </a>
          </li>
          <li>
            <a href="https://sentry.io/" target="_blank" rel="noreferrer">
              Sentry
            </a>
          </li>
          <li>
            <a href={RUNBOOK_URL} target="_blank" rel="noreferrer">
              Weekly GTM runbook
            </a>
          </li>
          <li>
            <Link to="/revenue">Revenue</Link>
          </li>
          <li>
            <Link to="/growth">Growth</Link>
          </li>
        </ul>
      </section>
    </ModuleShell>
  );
}
